export interface INodeCSSRule {
	rule: string
	index: number
	targetStyle: string
}

export interface INodeCSSRuleMin {
	r: string
	i: number
	t: string
}

export enum INodeMeta {
	IMG = '0',
	VIDEO = '1'
}

export interface IMGMeta {
	type: INodeMeta.IMG
	width: number,
	height: number,
}

export interface IMGMetaMin {
	type: INodeMeta.IMG
	w: number,
	h: number,
}

export interface VideoMeta {
	type: INodeMeta.VIDEO
	width: number,
	height: number,
}

export interface VideoMetaMin {
	type: INodeMeta.VIDEO
	w: number,
	h: number,
}

export interface INode {
	id?: string
	nodeName: string
	tagName: string
	attributes: Record<string, string>
	UUID: string
	childNodes: INode[],
	cssRules: INodeCSSRule[] // only for style elements
	isSVG: boolean,
	nodeType: number,
	meta?: IMGMeta | VideoMeta
}

export interface INodeMin {
	i?: string
	nn: string
	t: string
	a: Record<string, string>
	u: string
	c: INodeMin[]
	r: INodeCSSRuleMin[]
	s: boolean
	nt: number
	m?: IMGMetaMin | VideoMetaMin
}

export enum ChangeType {
	WINDOW_RESIZE = '1',
	VIEWPORT_RESIZE_EXPERIMENTAL = '1.1e', // experimental, not used
	CUSTOM_EVENT = '2',
	DOM_READY = '3',
	ADD_NODES = '4',
	ATTR_CHANGE = '5',
	REMOVE_NODES = '6',
	SCROLL = '7',
	FOCUS = '8',
	MOUSE_MOVE = '9',
	TOUCH = '9.1e', // experimental, not used
	MOUSE_CLICK = '10',
	MOUSE_OVER = '11',
	MOUSE_OUT = '12',
	PAGE_LOAD = '13',
	CHARACTER_DATA_CHANGE = '14',
	CSS_INSERT_RULE = '15',
	CSS_DELETE_RULE = '16',
	URL_CHANGE = '17',
	INPUT_CHANGE = '18',
	CHECKED_CHANGE = '18.c',
	USER_PROPERTY = '19',
	EVENT = '20',
	CLIENT_IP = '21',
	MEDIA_INFO = '22',
	MOUSE_DOUBLE_CLICK = '23',
	GESTURE = '24',
	CANVAS_FRAME_EXPERIMENTAL = '25', // experimental, not used
}


export interface CanvasFrame {
	seq: number,
	time: number,
	type: 'CANVAS_FRAME_EXPERIMENTAL',
	target: string
	data: string
}

export interface CanvasFrameMin {
	s: number
	t: number
	tg: string
	d: string
	ct: ChangeType.CANVAS_FRAME_EXPERIMENTAL
}

export enum GestureType {
	SWIPE_UP = '1',
	SWIPE_DOWN = '2',
	SWIPE_LEFT = '3',
	SWIPE_RIGHT = '4',
	TRAIL = '5',
}

export interface MediaInfo {
	seq: number,
	time: number,
	type: 'MEDIA_INFO',
	target: string
	meta: IMGMeta | VideoMeta
}

export interface MediaInfoMin {
	s: number
	t: number
	tg: string
	m: IMGMetaMin | VideoMetaMin
	ct: ChangeType.MEDIA_INFO
}

export interface WindowResize {
	seq: number,
	time: number,
	type: 'WINDOW_RESIZE'
	width: number
	height: number
}

export interface WindowResizeMin {
	s: number
	t: number
	w: number
	h: number
	ct: ChangeType.WINDOW_RESIZE
}

export interface ViewPortResize {
	seq: number,
	time: number,
	type: 'VIEWPORT_RESIZE_EXPERIMENTAL'
	width: number
	height: number
	scale: number
	pageLeft: number
	pageTop: number
	offsetLeft: number
	offsetTop: number
}

export interface ViewPortResizeMin {
	s: number
	t: number
	w: number
	h: number
	sc: number
	pl: number
	pt: number
	ol: number
	ot: number
	ct: ChangeType.VIEWPORT_RESIZE_EXPERIMENTAL,
}

export interface CustomEvent {
	seq: number,
	time: number,
	type: 'CUSTOM_EVENT'
	name: string
	payload: unknown;
}

export interface CustomEventMin {
	s: number
	t: number
	n: string
	p: unknown
	ct: ChangeType.CUSTOM_EVENT
}

export interface DomReady {
	seq: number,
	time: number,
	type: 'DOM_READY'
}

export interface DomReadyMin {
	s: number
	t: number
	ct: ChangeType.DOM_READY
}

export interface AddNodes {
	seq: number,
	time: number,
	type: 'ADD_NODES'
	parentUUID: string;
	children: INode[];
	prevUUID: string | null;
	nextUUID: string | null;
}

export interface AddNodesMin {
	s: number
	t: number
	p: string
	c: INodeMin[]
	pu: string | null
	nu: string | null
	ct: ChangeType.ADD_NODES
}

export interface AttrChange {
	seq: number,
	time: number,
	type: 'ATTR_CHANGE'
	target: string
	attr: string
	value: string | null
}

export interface AttrChangeMin {
	s: number
	t: number
	tg: string
	a: string
	v: string | null
	ct: ChangeType.ATTR_CHANGE
}

export interface RemoveNodes {
	seq: number,
	time: number,
	type: 'REMOVE_NODES'
	UUIDs: string[]
}

export interface RemoveNodesMin {
	s: number
	t: number
	u: string[]
	ct: ChangeType.REMOVE_NODES
}

export interface ScrollChange {
	seq: number,
	time: number,
	type: 'SCROLL'
	target: string
	scrollTop: number
	scrollLeft: number
}

export interface ScrollChangeMin {
	s: number
	t: number
	tg: string
	st: number
	sl: number
	ct: ChangeType.SCROLL
}

export interface FocusChange {
	seq: number,
	time: number,
	type: 'FOCUS'
	target: string
}

export interface FocusChangeMin {
	s: number
	t: number
	tg: string
	ct: ChangeType.FOCUS
}

export interface MouseMoveChange {
	seq: number,
	time: number,
	type: 'MOUSE_MOVE'
	target: string
	x: number
	y: number
}

export interface MouseMoveChangeMin {
	s: number
	t: number
	tg: string
	x: number
	y: number
	ct: ChangeType.MOUSE_MOVE
}

export interface MouseClickChange {
	seq: number,
	time: number,
	type: 'MOUSE_CLICK'
	target: string,
	selector: string,
	isResponsive: boolean,
	x: number,
	y: number,
	activity: string,
	isRage: boolean,
}

export interface MouseClickChangeMin {
	s: number
	t: number
	tg: string
	sel: string
	ires: boolean
	ct: ChangeType.MOUSE_CLICK,
	x: number
	y: number
	a: string
	rg: boolean
}

export interface MouseDoubleClickChange {
	seq: number,
	time: number,
	type: 'MOUSE_DOUBLE_CLICK'
	target: string,
	selector: string,
	isResponsive: boolean,
	x: number,
	y: number,
	activity: string,
	isRage: boolean,
}

export interface MouseDoubleClickChangeMin {
	s: number
	t: number
	tg: string
	sel: string
	ires: boolean
	ct: ChangeType.MOUSE_DOUBLE_CLICK,
	x: number
	y: number
	a: string
	rg: boolean
}

export interface MouseOverChange {
	seq: number,
	time: number,
	type: 'MOUSE_OVER'
	target: string
}

export interface MouseOverChangeMin {
	s: number
	t: number
	tg: string
	ct: ChangeType.MOUSE_OVER
}

export interface MouseOutChange {
	seq: number,
	time: number,
	type: 'MOUSE_OUT'
	target: string
}

export interface MouseOutChangeMin {
	s: number
	t: number
	tg: string
	ct: ChangeType.MOUSE_OUT
}

export interface PageLoad {
	seq: number,
	time: number,
	node: INode,
	type: 'PAGE_LOAD',
	url: string,
	utm: UTMData,
	topScroll: number,
	leftScroll: number,
}

export interface PageLoadMin {
	s: number
	t: number
	n: INodeMin
	u: string
	ct: ChangeType.PAGE_LOAD,
	utm: UTMData,
	ts: number
	ls: number
}

export interface CharacterDataChange {
	seq: number,
	time: number,
	type: 'CHARACTER_DATA_CHANGE'
	target: string
	textContent: string
}

export interface CharacterDataChangeMin {
	s: number
	t: number
	tg: string
	tc: string
	ct: ChangeType.CHARACTER_DATA_CHANGE
}

export interface CSSInsertRule {
	seq: number,
	time: number,
	type: 'CSS_INSERT_RULE'
	rule: string
	index: number
	targetStyle: string
}

export interface CSSInsertRuleMin {
	s: number
	t: number
	r: string
	i: number
	tg: string
	ct: ChangeType.CSS_INSERT_RULE
}

export interface CSSDeleteRule {
	seq: number,
	time: number,
	type: 'CSS_DELETE_RULE'
	index: number
	targetStyle: string
}

export interface CSSDeleteRuleMin {
	s: number
	t: number
	i: number
	tg: string
	ct: ChangeType.CSS_DELETE_RULE
}

export interface URLChange {
	seq: number,
	time: number,
	type: 'URL_CHANGE',
	url: string,
	utm: UTMData,
}

export interface URLChangeMin {
	s: number
	t: number
	u: string
	ct: ChangeType.URL_CHANGE
	utm: UTMDataMin,
}

export interface InputChange {
	seq: number,
	time: number,
	type: 'INPUT_CHANGE',
	target: string
	value: string
}

export interface InputChangeMin {
	s: number
	t: number
	tg: string
	v: string
	ct: ChangeType.INPUT_CHANGE
}

export interface CheckedChange {
	seq: number,
	time: number,
	type: 'CHECKED_CHANGE',
	target: string
	checked: boolean
}

export interface CheckedChangeMin {
	s: number
	t: number
	tg: string
	c: boolean
	ct: ChangeType.CHECKED_CHANGE
}

export interface UserProperty {
	type: 'USER_PROPERTY',
	key: string,
	value: unknown,
	time: number,
	seq: number,
}

export interface UserPropertyMin {
	t: number
	s: number
	k: string
	v: unknown
	ct: ChangeType.USER_PROPERTY
}

export interface SessionEvent {
	type: 'EVENT',
	name: string,
	properties: Record<string, unknown>,
	time: number,
	seq: number,
}

export interface SessionEventMin {
	t: number
	s: number
	n: string
	p: Record<string, unknown>
	ct: ChangeType.EVENT
}

export interface TouchChange {
	seq: number,
	time: number,
	type: 'TOUCH_EXPERIMENTAL',
	target: string
	ox: number
	oy: number
	x: number
	y: number
}

export interface TouchChangeMin {
	s: number
	t: number
	ct: ChangeType.TOUCH
	tg: string
	ox: number
	oy: number
	x: number
	y: number
}

export interface Gesture {
	seq: number,
	time: number,
	type: 'GESTURE',
	gestureType: GestureType,
	x: number,
	y: number,
	points: [number, number][],
}

export interface GestureMin {
	s: number
	t: number
	ct: ChangeType.GESTURE,
	gt: GestureType,
	x: number
	y: number
	p: [number, number][]
}

export type UTMData = Record<string, string>

export type UTMDataMin = UTMData

export interface DeviceData {
	osType: string, // 'Windows' | 'Mac OS' | 'Linux' | 'iOS' | 'Android'
	osVersion: string,
	deviceType: string, // 'Desktop' | 'Mobile' | 'Tablet',
	displayWidth: number,
	displayHeight: number,
	displayDpi: number,
	deviceClass: string, // 'Desktop' | 'Mobile' | 'Tablet',
	deviceManufacturer: string,
	deviceModel: string,
	browser: string,
	browserVersion: string,
	deviceLanguage: string,
	networkType: string,
	sdkVersion: string,
	sdkVersionNumber: number,
	appVersion: string,
	utm: UTMData,
	referer: string | null,
	userAgent: string,
}

export interface DeviceDataMin {
	ot: string
	ov: string
	dt: string
	dw: number
	dh: number
	dd: number
	dc: string
	dm: string
	dmo: string
	br: string
	bv: string
	dl: string
	nt: string
	sv: string
	svn: number
	av: string,
	utm: UTMDataMin,
	ref: string | null,
	ua: string,
}

export interface ClientIp {
	type: 'CLIENT_IP',
	ip: string,
	time: number,
	seq: number,
}

export interface ClientIpMin {
	t: number
	s: number
	ip: string
	ct: ChangeType.CLIENT_IP
}

export type CSSChanges = CSSInsertRule | CSSDeleteRule
export type CSSChangesMin = CSSInsertRuleMin | CSSDeleteRuleMin

export type DOMChanges = AddNodes | AttrChange | RemoveNodes | ScrollChange | FocusChange | MouseMoveChange | MouseClickChange | MouseOverChange | MouseOutChange | PageLoad | CharacterDataChange | WindowResize | CSSChanges | URLChange | InputChange | UserProperty | SessionEvent | ClientIp | DomReady | MediaInfo | MouseDoubleClickChange | ViewPortResize | TouchChange | Gesture | CanvasFrame | CheckedChange
export type DOMChangesMin = AddNodesMin | AttrChangeMin | RemoveNodesMin | ScrollChangeMin | FocusChangeMin | MouseMoveChangeMin | MouseClickChangeMin | MouseOverChangeMin | MouseOutChangeMin | PageLoadMin | CharacterDataChangeMin | WindowResizeMin | CSSChangesMin | URLChangeMin | InputChangeMin | UserPropertyMin | SessionEventMin | ClientIpMin | DomReadyMin | MediaInfoMin | MouseDoubleClickChangeMin | ViewPortResizeMin | TouchChangeMin | GestureMin | CanvasFrameMin | CheckedChangeMin
